<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-wrap items-center gap-4">
      <div class="w-[260px]">
        <SearchInput
          v-model="searchText"
          data-testid="check-validation-search-input"
          label="Buscar por médico ou CRM"
          width="100%"
          hide-details
          density="compact"
          @input="handleSearch"
        />
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <div class="relative" @click.stop="toggleFilter('status')">
          <SelectFilter
            data-testid="check-validation-status-filter"
            label="Status"
            :is-open="openFilterName === 'status'"
            :options="CHECK_VALIDATION_STATUS_FILTER_OPTIONS"
            :icon="statusIcon"
            :selected-value="statuses"
            dropdown-width="w-64"
            multiple
            @select="handleStatusSelect"
          />
        </div>

        <div class="relative" @click.stop="toggleFilter('period')">
          <SelectFilter
            data-testid="check-validation-period-filter"
            label="Período"
            :is-open="openFilterName === 'period'"
            :options="periodOptions"
            :icon="calendarIcon"
            :selected-value="periodDays === null ? null : String(periodDays)"
            dropdown-width="w-64"
            @select="handlePeriodSelect"
          />
        </div>

        <div class="relative" @click.stop="toggleFilter('unit')">
          <SelectFilter
            data-testid="check-validation-unit-filter"
            label="Unidades"
            :is-open="openFilterName === 'unit'"
            :options="unitOptions"
            :icon="hospitalIcon"
            :selected-value="selectedUnitIds"
            dropdown-width="w-64"
            searchable
            multiple
            @select="handleUnitSelect"
          />
        </div>

        <div class="relative" @click.stop="toggleFilter('schedule')">
          <SelectFilter
            data-testid="check-validation-schedule-filter"
            label="Escala"
            :is-open="openFilterName === 'schedule'"
            :options="scheduleOptions"
            :icon="scheduleIcon"
            :selected-value="selectedScheduleIds"
            dropdown-width="w-64"
            searchable
            multiple
            @select="handleScheduleSelect"
          />
        </div>
      </div>
    </div>

    <!-- Os SelectFilter só mostram um contador; os chips dizem o que está aplicado e
         permitem remover um filtro por vez. Os defaults da aba também são chips;
         "Limpar filtros" só aparece quando algo difere deles. -->
    <ActiveFilters
      :filters="activeFilterTags"
      :show-clear-button="activeFilterTags.length > 0"
      test-id-prefix="check-validation-active-filters"
      @remove="handleRemoveTag"
      @clear-all="handleClearAll"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, h, onBeforeUnmount, onMounted, ref, watch, type Component } from 'vue'
import { storeToRefs } from 'pinia'
import {
  mdiFormatListChecks,
  mdiCalendarClock,
  mdiOfficeBuildingOutline,
  mdiCalendarEdit
} from '@mdi/js'

import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import ActiveFilters, { FilterTag } from '@/components/Filters/ActiveFilters.vue'
import SelectFilter from '@/components/Filters/SelectFilter.vue'
import type { CheckValidationStatus } from '@/Network/Types/Responses/CheckValidationResponses'
import {
  CHECK_VALIDATION_PERIOD_OPTIONS,
  CHECK_VALIDATION_STATUS_FILTER_OPTIONS
} from '@/constants/check-validation.constants'
import type { CheckValidationPeriodDays } from '@/constants/check-validation.constants'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'
import { useScheduleStore } from '@/pinia-store/useScheduleStore'

import { buildCheckValidationFilterTags } from '../filterTags'

function mdiIcon(d: string): Component {
  return {
    render: () =>
      h('svg', { class: 'w-4 h-4', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' }, [
        h('path', { d, fill: '#7F7F7F' })
      ])
  }
}

const statusIcon = mdiIcon(mdiFormatListChecks)
const calendarIcon = mdiIcon(mdiCalendarClock)
const hospitalIcon = mdiIcon(mdiOfficeBuildingOutline)
const scheduleIcon = mdiIcon(mdiCalendarEdit)

const SEARCH_DEBOUNCE_MS = 400

// A barra é CONTROLADA pelo store (props = estado aplicado; emits = intenção):
// assim os chips nunca divergem do que foi de fato para a query, e "limpar"
// não precisa remontar a barra. Só o texto da busca tem estado local (debounce).
const props = defineProps<{
  search: string
  statuses: CheckValidationStatus[]
  periodDays: CheckValidationPeriodDays | null
  healthUnitIds: number[]
  scheduleIds: number[]
}>()

const emit = defineEmits<{
  search: [query: string]
  filterStatuses: [statuses: CheckValidationStatus[]]
  filterPeriod: [days: CheckValidationPeriodDays]
  filterUnits: [healthUnitIds: number[]]
  filterSchedules: [scheduleIds: number[]]
  clearFilters: []
}>()

// `value` é string — contrato do SelectFilter (os emits convertem para number).
const scheduleStore = useScheduleStore()
const { listSchedules } = storeToRefs(scheduleStore)

const healthUnitStore = useHealthUnitStore()
const { listHealthUnits } = storeToRefs(healthUnitStore)

const unitOptions = computed(() =>
  listHealthUnits.value.map((unit) => ({ label: unit.title, value: unit.value }))
)

const scheduleOptions = computed(() =>
  listSchedules.value.map((schedule) => ({ label: schedule.title, value: schedule.value }))
)

const periodOptions = CHECK_VALIDATION_PERIOD_OPTIONS.map((option) => ({
  label: option.label,
  value: String(option.value)
}))

const selectedUnitIds = computed(() => props.healthUnitIds.map(String))
const selectedScheduleIds = computed(() => props.scheduleIds.map(String))

const searchText = ref(props.search)
// Reset externo (clearFilters no store) também limpa o campo.
watch(
  () => props.search,
  (value) => {
    if (value !== searchText.value) searchText.value = value
  }
)

const openFilterName = ref<string | null>(null)

const activeFilterTags = computed(() =>
  buildCheckValidationFilterTags({
    search: props.search,
    statuses: props.statuses,
    onlyOverdue: false,
    periodDays: props.periodDays,
    unitIds: selectedUnitIds.value,
    scheduleIds: selectedScheduleIds.value,
    unitOptions: unitOptions.value,
    scheduleOptions: scheduleOptions.value
  })
)

function handleClickOutside() {
  openFilterName.value = null
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  // Erros já são tratados dentro das actions dos stores de opções.
  void Promise.all([
    scheduleStore.getAllSchedules(),
    healthUnitStore.fetchAllHealthUnitsForSelect()
  ])
})

onBeforeUnmount(() => {
  if (searchDebounce) clearTimeout(searchDebounce)
  document.removeEventListener('click', handleClickOutside)
})

function toggleFilter(name: string) {
  openFilterName.value = openFilterName.value === name ? null : name
}

let searchDebounce: ReturnType<typeof setTimeout> | null = null

function handleSearch() {
  if (searchDebounce) clearTimeout(searchDebounce)
  searchDebounce = setTimeout(() => emit('search', searchText.value), SEARCH_DEBOUNCE_MS)
}

function toggleSelection<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value]
}

// O cast é seguro: os values das opções são os do enum da API.
function handleStatusSelect(value: string) {
  emit('filterStatuses', toggleSelection(props.statuses, value as CheckValidationStatus))
}

function handlePeriodSelect(value: string) {
  emit('filterPeriod', Number(value) as CheckValidationPeriodDays)
}

function handleUnitSelect(value: string) {
  emit('filterUnits', toggleSelection(props.healthUnitIds, Number(value)))
}

function handleScheduleSelect(value: string) {
  emit('filterSchedules', toggleSelection(props.scheduleIds, Number(value)))
}

function clearSearch() {
  // Cancela um debounce em voo: sem isso o timer pendente dispararia um segundo
  // fetch redundante depois da limpeza.
  if (searchDebounce) clearTimeout(searchDebounce)
  searchText.value = ''
}

// O ActiveFilters é genérico (`id: string | number`, `type: string`); o chip
// carrega o id qualificado (`unit-1`) e o SelectFilter trabalha com o id cru.
function handleRemoveTag(tag: FilterTag) {
  const optionId = String(tag.id).replace(`${tag.type}-`, '')

  if (tag.type === 'search') {
    clearSearch()
    emit('search', '')
  } else if (tag.type === 'status') {
    handleStatusSelect(optionId)
  } else if (tag.type === 'period') {
    // Remover o chip de período volta ao preset default — sem período a fila
    // traria o histórico inteiro, que não é o que "remover" quer dizer aqui.
    emit('filterPeriod', CHECK_VALIDATION_PERIOD_OPTIONS[0].value)
  } else if (tag.type === 'unit') {
    handleUnitSelect(optionId)
  } else if (tag.type === 'schedule') {
    handleScheduleSelect(optionId)
  }
}

// Delega ao pai o reset do store num único fetch — remover chip por chip
// dispararia uma request por filtro.
function handleClearAll() {
  clearSearch()
  openFilterName.value = null
  emit('clearFilters')
}
</script>
